import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { CellPickerProps, MenuPosition } from './CellPicker.types';

const MENU_MAX = 240;
// The wizard's fixed footer bar (Preview / Cancel) covers the bottom of the window.
const FOOTER = 64;
const BLANK = '(blank)';

export const splitValues = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

// Ticks an option on or off; values that are not options (an invalid imported cell) drop out.
export const toggleValue = (
  value: string,
  option: string,
  options: string[]
): string => {
  const current = splitValues(value).filter((item) => options.includes(item));
  const next = current.includes(option)
    ? current.filter((item) => item !== option)
    : [...current, option];
  return next.join(', ');
};

// An Adjust cell's closed list, open as soon as the cell is edited. The menu is portalled to the body with fixed
// coordinates, so the grid's clipped cells and scroll container never hide it; it flips above near the bottom.
const CellPicker: React.FC<CellPickerProps> = ({
  value,
  options,
  multiple = false,
  invalid = false,
  onChange,
}) => {
  const [open, setOpen] = useState(true);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const selected = multiple ? splitValues(value) : [value];
  const choices = multiple ? options : ['', ...options];

  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - FOOTER - rect.bottom;
    const above = rect.top - 8;
    const flip = below < Math.min(MENU_MAX, 160) && above > below;
    const maxHeight = Math.min(MENU_MAX, flip ? above : below);
    setPosition({
      top: flip ? rect.top - maxHeight - 2 : rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 180),
      maxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    trigger.current?.focus();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !menu.current?.contains(target))
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) =>
      event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const pick = (option: string) => {
    if (multiple) {
      onChange(toggleValue(value, option, options));
    } else {
      onChange(option);
      setOpen(false);
    }
  };

  return (
    <>
      <button
        type='button'
        ref={trigger}
        className={classnames('importWizardCellPicker', { open, invalid })}
        onClick={() => setOpen(!open)}
        aria-haspopup='listbox'
        aria-expanded={open}
      >
        <span className='value'>{value || BLANK}</span>
      </button>
      {open &&
        position &&
        createPortal(
          <ul
            ref={menu}
            className='importWizardCellPickerMenu'
            role='listbox'
            aria-multiselectable={multiple}
            style={position}
          >
            {choices.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <li
                  key={option || BLANK}
                  role='option'
                  aria-selected={isSelected}
                  className={classnames({
                    selected: isSelected,
                    blank: !option,
                  })}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pick(option)}
                >
                  {multiple && <span className='tick' />}
                  {option || BLANK}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
};

export default CellPicker;
